import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import {socketClient}  from '../index';

let activeSingleUserUpdateListener = null;

const customerHistoryTypes = ['invoices', 'quotes', 'vehicles'];
const defaultHistoryEntry = {
  items: [],
  pagination: {
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
    hasPrev: false,
    hasNext: false
  },
  loading: false,
  search: '',
  error: null
};
const createDefaultCustomerHistories = () => customerHistoryTypes.reduce((acc, type) => {
  acc[type] = { ...defaultHistoryEntry, pagination: { ...defaultHistoryEntry.pagination } };
  return acc;
}, {});

const emitScopedSocketRequest = ({
  emitEvent,
  successEvent,
  errorEvent,
  payload,
  timeoutMessage,
  mapSuccess
}) => new Promise((resolve, reject) => {
  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    resolve(mapSuccess ? mapSuccess(response) : response);
  };

  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    reject(new Error(response?.message || timeoutMessage));
  };

  const timeoutId = setTimeout(() => {
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    reject(new Error(timeoutMessage));
  }, 10000);

  socketClient.on(successEvent, handleSuccess);
  socketClient.on(errorEvent, handleError);
  socketClient.emit(emitEvent, payload);
});

const initialState = {
  shopData: {},
  searchTerm: '',
  users: [],
  singleUser: null,
  customerHistories: createDefaultCustomerHistories()
};

export const getFirebaseUser = createAsyncThunk('getFirebaseUser', async function () {
  return firebase.auth().currentUser;
}); 

export const setSearchTerm = createAsyncThunk('setSearchTerm', async function (searchTerm, { dispatch }) {
  dispatch({ type: 'users/setSearchTerm', payload: searchTerm });
});

export const requestGetAllUsers = createAsyncThunk('requestGetAllUsers', async function (_, { rejectWithValue }) {
  try {
    const requestId = `customers:list:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const idToken = await firebase.auth().currentUser.getIdToken();

    return await emitScopedSocketRequest({
      emitEvent: 'getCustomers',
      successEvent: 'getCustomers',
      errorEvent: 'getCustomersError',
      payload: { idToken, requestId },
      timeoutMessage: 'Timed out while loading customers',
      mapSuccess: (response) => response?.customers || []
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Unable to load customers');
  }
});

export const getSingleUser = createAsyncThunk('getSingleUser', async function (customerId, { rejectWithValue }) {
  try {
    const idToken = await firebase.auth().currentUser.getIdToken();
    return await new Promise((resolve, reject) => {
      const requestId = `single-customer:${customerId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

      const handleSuccess = (response) => {
        if (response?.requestId !== requestId) {
          return;
        }

        clearTimeout(timeoutId);
        socketClient.off('getCustomer', handleSuccess);
        socketClient.off('getCustomerError', handleError);
        resolve(response?.customer || null);
      };

      const handleError = (response) => {
        if (response?.requestId !== requestId) {
          return;
        }

        clearTimeout(timeoutId);
        socketClient.off('getCustomer', handleSuccess);
        socketClient.off('getCustomerError', handleError);
        reject(new Error(response?.message || 'Unable to load customer profile'));
      };

      const timeoutId = setTimeout(() => {
        socketClient.off('getCustomer', handleSuccess);
        socketClient.off('getCustomerError', handleError);
        reject(new Error('Timed out while loading customer profile'));
      }, 10000);

      socketClient.on('getCustomer', handleSuccess);
      socketClient.on('getCustomerError', handleError);
      socketClient.emit('getCustomer', { id: customerId, requestId, idToken });
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Unable to load customer profile');
  }
});

export const searchCustomer = createAsyncThunk('searchCustomer', async function (search, {dispatch, rejectWithValue}) {
  try {
    dispatch({ type: 'users/setSearchTerm', payload: search });

    if (!String(search || '').trim()) {
      return await dispatch(requestGetAllUsers()).unwrap();
    }

    const requestId = `customers:search:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const idToken = await firebase.auth().currentUser.getIdToken();

    return await emitScopedSocketRequest({
      emitEvent: 'searchCustomer',
      successEvent: 'searchCustomer',
      errorEvent: 'searchCustomerError',
      payload: { idToken, search, requestId },
      timeoutMessage: 'Timed out while searching customers',
      mapSuccess: (response) => response?.customers || []
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Unable to search customers');
  }
});

export const createCustomer = createAsyncThunk('createCustomer', async function (data, { rejectWithValue }) {
  try {
    const requestId = `customers:create:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const idToken = await firebase.auth().currentUser.getIdToken();

    return await emitScopedSocketRequest({
      emitEvent: 'createCustomer',
      successEvent: 'createCustomer',
      errorEvent: 'createCustomerError',
      payload: { idToken, requestId, ...data },
      timeoutMessage: 'Timed out while creating customer',
      mapSuccess: (response) => response?.customer || null
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Unable to create customer');
  }
});

export const editCustomerProfile = createAsyncThunk('editCustomerProfile', async function (data) {
  socketClient.emit('editCustomerProfile', {idToken: await firebase.auth().currentUser.getIdToken(), ...data});
});

export const createVehicle = createAsyncThunk('createVehicle', async function (data) {
  socketClient.emit('addVehicle', {...data , idToken: await firebase.auth().currentUser.getIdToken()});
});

export const updateVehicle = createAsyncThunk('updateVehicle', async function (data) {
  const idToken = await firebase.auth().currentUser.getIdToken();
  return new Promise((resolve, reject) => {
    const vehicleId = Number(data.vehicleId || data.id || 0);
    const handler = (response) => {
      if (Number(response?.vehicleId || 0) !== vehicleId) {
        return;
      }
      clearTimeout(timeoutId);
      socketClient.off('vehicleUpdated', handler);
      resolve(response);
    };

    const timeoutId = setTimeout(() => {
      socketClient.off('vehicleUpdated', handler);
      reject(new Error('Timed out while updating vehicle'));
    }, 10000);

    socketClient.on('vehicleUpdated', handler);
    socketClient.emit('updateVehicle', {
      ...data,
      vehicleId,
      idToken
    });
  });
});

export const createNewQuote = createAsyncThunk('createNewQuote', async function (data) {
  const idToken = await firebase.auth().currentUser.getIdToken();
  return new Promise((resolve, reject) => {
    const requestId = `new-quote:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const handleSuccess = (response) => {
      if (response?.requestId && response.requestId !== requestId) {
        return;
      }

      clearTimeout(timeoutId);
      socketClient.off('newQuote', handleSuccess);
      resolve(response);
    };

    const timeoutId = setTimeout(() => {
      socketClient.off('newQuote', handleSuccess);
      reject(new Error('Timed out while creating quote'));
    }, 10000);

    socketClient.on('newQuote', handleSuccess);
    socketClient.emit('newQuote', {
      ...data,
      requestId,
      idToken
    });
  });
});

export const getShopData = createAsyncThunk('getShopData', async function () {
  socketClient.emit('getShopData', { idToken: await firebase.auth().currentUser.getIdToken() });
});

export const setVehicleForSale = createAsyncThunk('setVehicleForSale', async function ({vehicleId, customerId}, { dispatch }) {
  socketClient.emit('setVehicleForSale', { vehicleId, idToken: await firebase.auth().currentUser.getIdToken() });
  dispatch(getSingleUser(customerId));
});

export const unsubscribeSingleUser = createAsyncThunk('unsubscribeSingleUser', async function () {
  if (activeSingleUserUpdateListener) {
    socketClient.off('updateCustomers', activeSingleUserUpdateListener);
    activeSingleUserUpdateListener = null;
  }
});

export const fetchShopData = createAsyncThunk(
  'fetchShopData',
  async function (_, { dispatch }) {
    socketClient.on('getCustomers', (response) => {
      dispatch({ type: 'users/fetchShopData', payload: response });
    });
});

export const getCustomerHistory = createAsyncThunk(
  'users/getCustomerHistory',
  async function ({ customerId, historyType, page = 1, pageSize = 10, search = '' }, { rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      return await new Promise((resolve, reject) => {
        const requestId = `customer-history:${historyType}:${customerId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
        const handleSuccess = (response) => {
          if (response?.requestId !== requestId) {
            return;
          }

          clearTimeout(timeoutId);
          socketClient.off('getCustomerHistory', handleSuccess);
          socketClient.off('getCustomerHistoryError', handleError);
          resolve(response);
        };
        const handleError = (response) => {
          if (response?.requestId !== requestId) {
            return;
          }

          clearTimeout(timeoutId);
          socketClient.off('getCustomerHistory', handleSuccess);
          socketClient.off('getCustomerHistoryError', handleError);
          reject(new Error(response?.message || 'Unable to load customer history'));
        };
        const timeoutId = setTimeout(() => {
          socketClient.off('getCustomerHistory', handleSuccess);
          socketClient.off('getCustomerHistoryError', handleError);
          reject(new Error('Timed out while loading customer history'));
        }, 10000);

        socketClient.on('getCustomerHistory', handleSuccess);
        socketClient.on('getCustomerHistoryError', handleError);
        socketClient.emit('getCustomerHistory', {
          customerId,
          historyType,
          page,
          pageSize,
          search,
          requestId,
          idToken
        });
      });
    } catch (error) {
      return rejectWithValue({
        historyType,
        message: error.message || 'Unable to load customer history'
      });
    }
  }
);

export const fetchSingleUser = createAsyncThunk(
  'fetchSingleUser',
  async function (customerId, { dispatch }) {
    if (activeSingleUserUpdateListener) {
      socketClient.off('updateCustomers', activeSingleUserUpdateListener);
    }

    activeSingleUserUpdateListener = (response) => {
      if (Number(response?.id || 0) !== Number(customerId || 0)) {
        return;
      }

      dispatch({ type: 'users/fetchSingleUser', payload: response });
    };

    socketClient.on('updateCustomers', activeSingleUserUpdateListener);
  });

const slice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    fetchShopData(state, action) {
      state.shopData = action.payload;
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    fetchSingleUser(state, action) {
      state.singleUser = action.payload || null;
    },
    resetCustomerHistories(state) {
      state.customerHistories = createDefaultCustomerHistories();
    }
  },
  extraReducers: (builder) => {
    builder.addCase(requestGetAllUsers.pending, (state) => {
      state.requestGetAllUsersStatus = 'Pending';
    });
    builder.addCase(requestGetAllUsers.fulfilled, (state, action) => {
      state.requestGetAllUsersStatus = 'Sent';
      state.users = action.payload || [];
    });
    builder.addCase(requestGetAllUsers.rejected, (state) => {
      state.requestGetAllUsersStatus = 'Rejected';
    });
    builder.addCase(searchCustomer.fulfilled, (state, action) => {
      state.users = action.payload || [];
    });
    builder.addCase(createCustomer.fulfilled, (state, action) => {
      state.singleUser = action.payload || null;
    });
    builder.addCase(getSingleUser.fulfilled, (state, action) => {
      state.singleUser = action.payload || null;
    });
    builder.addCase(getCustomerHistory.pending, (state, action) => {
      const historyType = action.meta.arg.historyType;
      if (!state.customerHistories[historyType]) {
        state.customerHistories[historyType] = { ...defaultHistoryEntry, pagination: { ...defaultHistoryEntry.pagination } };
      }
      state.customerHistories[historyType].loading = true;
      state.customerHistories[historyType].error = null;
      state.customerHistories[historyType].search = action.meta.arg.search || '';
    });
    builder.addCase(getCustomerHistory.fulfilled, (state, action) => {
      const historyType = action.payload.historyType;
      if (!state.customerHistories[historyType]) {
        state.customerHistories[historyType] = { ...defaultHistoryEntry, pagination: { ...defaultHistoryEntry.pagination } };
      }
      state.customerHistories[historyType].items = action.payload.items || [];
      state.customerHistories[historyType].pagination = action.payload.pagination || { ...defaultHistoryEntry.pagination };
      state.customerHistories[historyType].loading = false;
      state.customerHistories[historyType].error = null;
      state.customerHistories[historyType].search = action.meta.arg.search || '';
    });
    builder.addCase(getCustomerHistory.rejected, (state, action) => {
      const historyType = action.payload?.historyType || action.meta.arg.historyType;
      if (!state.customerHistories[historyType]) {
        state.customerHistories[historyType] = { ...defaultHistoryEntry, pagination: { ...defaultHistoryEntry.pagination } };
      }
      state.customerHistories[historyType].loading = false;
      state.customerHistories[historyType].error = action.payload?.message || action.error?.message || 'Unable to load customer history';
    });
  },
});

export const reducer = slice.reducer;
export const { resetCustomerHistories } = slice.actions;
